import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import logoHitchef from "@/assets/logo-hitchef.png";
import { trackEvent } from "@/lib/analytics";

const Start = () => {
  const [searchParams] = useSearchParams();
  const [botLink, setBotLink] = useState("https://t.me/hitchef_bot");

  useEffect(() => {
    const utmSource = searchParams.get("utm_source") || "";
    const utmMedium = searchParams.get("utm_medium") || "";
    const utmCampaign = searchParams.get("utm_campaign") || "";
    const utmContent = searchParams.get("utm_content") || "";
    const utmTerm = searchParams.get("utm_term") || "";

    const utmParts = [utmSource, utmMedium, utmCampaign, utmContent, utmTerm].
    filter(Boolean).
    join("_");

    if (utmParts) {
      setBotLink(`https://t.me/hitchef_bot?start=utm_${utmParts}`);
      localStorage.setItem("hitchef_utm", JSON.stringify({
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        content: utmContent,
        term: utmTerm,
        timestamp: new Date().toISOString()
      }));
    }
  }, [searchParams]);

  const handleStart = () => {
    const utmData = {
      source: searchParams.get("utm_source") || undefined,
      utm_source: searchParams.get("utm_source") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      utm_content: searchParams.get("utm_content") || undefined,
    };
    trackEvent("start_hit_creation", utmData);
    window.open(botLink, "_blank");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      
      {/* Vibrant purple gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #9333ea 0%, #c026d3 30%, #a855f7 60%, #7c3aed 100%)'
        }} />

      
      {/* Animated floating elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Musical notes and stars */}
        <div
          className="absolute text-6xl opacity-20"
          style={{
            top: '10%',
            left: '8%',
            animation: 'floatStar 6s ease-in-out infinite'
          }}>

          ⭐
        </div>
        <div
          className="absolute text-5xl opacity-25"
          style={{
            top: '15%',
            right: '12%',
            animation: 'floatNote 5s ease-in-out infinite 0.5s'
          }}>

          🎵
        </div>
        <div
          className="absolute text-4xl opacity-15"
          style={{
            bottom: '20%',
            left: '5%',
            animation: 'floatStar 7s ease-in-out infinite 1s'
          }}>

          ✨
        </div>
        <div
          className="absolute text-5xl opacity-20"
          style={{
            bottom: '25%',
            right: '8%',
            animation: 'floatNote 6s ease-in-out infinite 1.5s'
          }}>

          🎶
        </div>
        <div
          className="absolute text-3xl opacity-25"
          style={{
            top: '45%',
            left: '3%',
            animation: 'floatStar 5s ease-in-out infinite 0.3s'
          }}>

          ⭐
        </div>
        <div
          className="absolute text-4xl opacity-20"
          style={{
            top: '60%',
            right: '5%',
            animation: 'floatNote 7s ease-in-out infinite 2s'
          }}>

          🎵
        </div>
        
        {/* Soft glow orbs */}
        <div
          className="absolute w-64 h-64 rounded-full blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)',
            top: '5%',
            right: '20%',
            animation: 'pulseOrb 4s ease-in-out infinite'
          }} />

        <div
          className="absolute w-48 h-48 rounded-full blur-2xl opacity-25"
          style={{
            background: 'radial-gradient(circle, #22d3ee 0%, transparent 70%)',
            bottom: '10%',
            left: '15%',
            animation: 'pulseOrb 5s ease-in-out infinite 1s'
          }} />

      </div>

      {/* Main content card */}
      <div
        className="relative z-10 w-full max-w-sm text-center animate-scale-in">

        {/* Logo with bounce effect */}
        <div className="flex justify-center mb-6">
          <div
            className="relative"
            style={{ animation: 'logoFloat 3s ease-in-out infinite' }}>

            {/* Glow behind logo */}
            <div
              className="absolute -inset-4 rounded-full blur-2xl opacity-60"
              style={{ background: 'radial-gradient(circle, #fbbf24 0%, #f97316 50%, transparent 70%)' }} />

            <img
              src={logoHitchef}
              alt="Хит Шеф"
              className="relative w-36 h-36 rounded-3xl object-cover"
              style={{
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(251, 191, 36, 0.3)'
              }} />

          </div>
        </div>

        {/* Title */}
        <h1
          className="text-4xl font-bold tracking-tight text-white mb-2 drop-shadow-lg"
          style={{ textShadow: '0 2px 20px rgba(0, 0, 0, 0.3)' }}>

          Хит Шеф
        </h1>

        {/* Subtitle with playful style */}
        <p
          className="text-xl font-semibold mb-4"
          style={{
            background: 'linear-gradient(90deg, #fef08a, #fbbf24, #fb923c)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: 'none'
          }}>
          ✨Удиви друзей и близких - создай песню для них за 3 минуты! ✨

        </p>

        {/* Description */}
        <p
          className="text-white/90 leading-relaxed mb-8 text-lg"
          style={{ textShadow: '0 1px 10px rgba(0, 0, 0, 0.2)' }}>
          Песня в пару кликов для дней рождений, свадеб и любых праздников 🎉

        </p>

        {/* CTA Button - bright and inviting */}
        <button
          onClick={handleStart}
          className="w-full h-16 text-xl font-bold rounded-2xl text-purple-900 transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #fef08a 0%, #fbbf24 50%, #f59e0b 100%)',
            boxShadow: '0 8px 32px rgba(251, 191, 36, 0.5), 0 0 20px rgba(251, 191, 36, 0.3)'
          }}>

          🎤 Создать хит
        </button>

        {/* Stats with fun style */}
        <p
          className="text-white/70 text-base mt-6 font-medium"
          style={{ textShadow: '0 1px 8px rgba(0, 0, 0, 0.2)' }}>
          🌟 7 000+ хитов создано

        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes floatStar {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(10deg); }
        }
        @keyframes floatNote {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(-5deg); }
        }
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
        @keyframes pulseOrb {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }
      `}</style>
    </div>);

};

export default Start;